import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import ICO_COMPUTER from 'assets/images/icons/computer-black.svg';
import ICO_PHONE from 'assets/images/icons/mobile-phone-black.svg';
import IMG_DOCK from 'assets/images/online-booking/dock.png';
import IMG_APP from 'assets/images/online-booking/phone-app.png';
import IMG_CARD from 'assets/images/online-booking/stripe.png';
import IMG_LOGO from 'assets/images/pos/slickpay-logo-purple.png';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import SM from 'components/sidebar/appointment/wizard/Wizard.module.scss';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { locationNew } from 'utils/dom/location.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';
import C from 'utils/values/constant.util';

import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';

import DeviceChecker from './DeviceChecker.component';
import DeviceRequirements from './DeviceRequirements.component';
import InternetRequirements from './InternetRequirements.component';
import NetworkSpeedChecker from './NetworkSpeedChecker.component';
import StaffToggleButtons from './StaffToggleButtons.component';

import CN from './UserNetwork.module.scss';

const RENDER_LINK = false;

const CONTENT_OPTIONS = C({
  staff_1_3: {
    device: 'Ipad Air',
    modelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.productYear',
    processor: 'Ultra 5',
    ram: '16GB',
    speed: '10 Mbps',
    connectionTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.wiFi',
  },
  staff_4_6: {
    device: 'Ipad Air / MacBook Air',
    modelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.productYear',
    processor: 'Ultra 5',
    ram: '16GB',
    speed: '15 Mbps',
    connectionTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.ethernetCable',
  },
  staff_7_10: {
    device: 'Apple MacBook',
    modelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.productYear',
    processor: 'Ultra 7',
    ram: '16GB',
    speed: '30 Mbps',
    connectionTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.ethernetCable',
  },
  staff_11_plus: {
    device: 'iMac',
    modelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.productYear',
    processor: 'Snapdragon X plus',
    ram: '16GB',
    speed: '40 Mbps',
    connectionTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.ethernetCable',
  },
});

const LINK_TBD = '#'; // TODO:
const LINK_TC = 'https://www.getslick.com/legal/terms';
const LINK_DOCK = 'https://gettingslick.typeform.com/to/JTVF4Q2c?utm_source=splashscreen';

const translateDataCard = (t) => [
  <div key="card_machine">
    <CurrencySign />
    {t('staffAndBusiness.userNetwork.otherHardware.section1Bullet1')}
  </div>,
  t('staffAndBusiness.userNetwork.otherHardware.section1Bullet2'),
  t('staffAndBusiness.userNetwork.otherHardware.section1Bullet3'),
];

const DATA_DOCK = C([
  'staffAndBusiness.userNetwork.otherHardware.section2Bullet1',
  'staffAndBusiness.userNetwork.otherHardware.section2Bullet2',
  'staffAndBusiness.userNetwork.otherHardware.section2Bullet3',
]);

const DATA_APP = C([
  'staffAndBusiness.userNetwork.otherHardware.section3Bullet1',
  'staffAndBusiness.userNetwork.otherHardware.section3Bullet2',
  'staffAndBusiness.userNetwork.otherHardware.section3Bullet3',
]);

const DATA_BANNER = C([
  'staffAndBusiness.userNetwork.hardwareInformation.performanceBanner.bullet1',
  'staffAndBusiness.userNetwork.hardwareInformation.performanceBanner.bullet2',
  'staffAndBusiness.userNetwork.hardwareInformation.performanceBanner.bullet3',
  'staffAndBusiness.userNetwork.hardwareInformation.performanceBanner.bullet4',
  'staffAndBusiness.userNetwork.hardwareInformation.performanceBanner.bullet5',
]);

const OPTIONS = [
  { id: 'staff_1_3', labelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.staffRange1_3' },
  { id: 'staff_4_6', labelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.staffRange4_6' },
  { id: 'staff_7_10', labelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.staffRange7_10' },
  { id: 'staff_11_plus', labelTranslationKey: 'staffAndBusiness.userNetwork.hardwareInformation.staffRange11Plus' },
];

const MODAL_CONTENT = C({
  deviceTest: {
    url: 'https://chromium.github.io/octane/',
    titleTranslationKey: 'staffAndBusiness.userNetwork.testModal.title',
    textTranslationKey: 'staffAndBusiness.userNetwork.testModal.deviceTestDescription',
  },
  internetTest: {
    url: 'https://fast.com/',
    titleTranslationKey: 'staffAndBusiness.userNetwork.testModal.title',
    textTranslationKey: 'staffAndBusiness.userNetwork.testModal.internetTestDescription',
  },
});

const UserNetwork = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const [selectedOption, setSelectedOption] = useState(null);
  const { val: isDropdownOpen, tip: toggleDropdown, nay: closeDropdown } = useTip(false);
  const { val: renderModal, yay: showModal, nay: hideModal } = useTip(false);
  const [modalContent, setModalContent] = useState({ titleTranslationKey: '', textTranslationKey: '', url: '' });

  const handleOpenModal = useCallback(
    (key) => {
      const content = MODAL_CONTENT[key];
      if (!content) return;

      setModalContent(content);
      showModal();

      delayed(DELAY.attention, () => {
        locationNew({ url: content.url });
      });
    },
    [showModal],
  );

  const translatedDataApp = DATA_APP.map((data) => t(data));
  const translatedDataDock = DATA_DOCK.map((data) => t(data));
  const translatedDataBanner = DATA_BANNER.map((data) => t(data));

  const translatedOptions = useMemo(
    () =>
      OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelTranslationKey),
      })),
    [t],
  );

  const dropdownSelectedOption = (selected) => translatedOptions.find((option) => option.id === selected)?.label ?? '';

  return (
    <div
      data-bem="UserNetwork"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <Foldout
        tag="UserNetwork__information"
        src={ICO_COMPUTER}
        title={t('staffAndBusiness.userNetwork.hardwareInformation.foldoutTitle')}
        isOpen={true}
        width="max"
      >
        <SectionSeparator />
        <div>
          <div
            className={cn({
              [CN.gap]: true,
              [CN.gapBottom]: selectedOption,
            })}
          >
            <div className={CN.text}>
              {t('staffAndBusiness.userNetwork.hardwareInformation.description1Part1')}
              <div className={CN.textBold}>
                {t('staffAndBusiness.userNetwork.hardwareInformation.description1Part2')}
              </div>
            </div>
            <div className={CN.text}>
              {t('staffAndBusiness.userNetwork.hardwareInformation.description2Part1')}{' '}
              <div className={CN.textBold}>
                {t('staffAndBusiness.userNetwork.hardwareInformation.description2Part2')}
              </div>{' '}
              {t('staffAndBusiness.userNetwork.hardwareInformation.description2Part3')}
            </div>
            <ResponsiveBox.Desktop>
              <div className={CN.label} data-bem="UserNetwork__desktop--size">
                {t('staffAndBusiness.userNetwork.hardwareInformation.chooseBusinessSize')}
              </div>
              <div className={CN.filter} data-bem="UserNetwork__desktop--quick-buttons">
                <StaffToggleButtons selectedOption={selectedOption} setSelectedOption={setSelectedOption} />
              </div>
            </ResponsiveBox.Desktop>
            <ResponsiveBox.Mobile>
              <div className={CN.label} data-bem="UserNetwork__mobile--size">
                {t('staffAndBusiness.userNetwork.hardwareInformation.chooseBusinessSize')}
              </div>
              <div className={CN.mobileDropdownContainer} data-bem="UserNetwork__mobile--dropdown">
                <Button variant="quinary" active={selectedOption} onClick={toggleDropdown} noMargin>
                  <div className={CN.buttonInner}>
                    <div className={CN.buttonInnerText}>
                      <Icon variant="building" width="sm" />
                      <span className={CN.withIcon}>
                        {selectedOption
                          ? t('staffAndBusiness.userNetwork.hardwareInformation.selected', {
                              option: dropdownSelectedOption(selectedOption),
                            })
                          : t('staffAndBusiness.userNetwork.hardwareInformation.chooseBusinessSize')}
                      </span>
                    </div>
                    <Icon variant="arrowTriangle" width="xs" />
                  </div>
                </Button>
                {isDropdownOpen && (
                  <DropdownFoldout width="max" position="bottom">
                    <DropdownFoldout.Body>
                      <div className={CN.filter}>
                        <StaffToggleButtons
                          selectedOption={selectedOption}
                          setSelectedOption={(option) => {
                            setSelectedOption(option);
                            closeDropdown();
                          }}
                        />
                      </div>
                    </DropdownFoldout.Body>
                  </DropdownFoldout>
                )}
              </div>
            </ResponsiveBox.Mobile>
            {!selectedOption && (
              <div className={CN.alert} data-bem="UserNetwork__alert">
                <div className={CN.alertTitle}>
                  {t('staffAndBusiness.userNetwork.hardwareInformation.bannerTitlePart1')}{' '}
                  <span className={CN.focused}>
                    {t('staffAndBusiness.userNetwork.hardwareInformation.bannerTitlePart2')}
                  </span>{' '}
                  {t('staffAndBusiness.userNetwork.hardwareInformation.bannerTitlePart3')}
                </div>
                <div className={CN.alertText}>{t('staffAndBusiness.userNetwork.hardwareInformation.bannerText')}</div>
              </div>
            )}
          </div>
          {selectedOption && (
            <div>
              <DeviceRequirements
                device={CONTENT_OPTIONS[selectedOption].device}
                model={t(CONTENT_OPTIONS[selectedOption].modelTranslationKey)}
                processor={CONTENT_OPTIONS[selectedOption].processor}
                ram={CONTENT_OPTIONS[selectedOption].ram}
              />
              <DeviceChecker selectedOption={selectedOption} onRunTest={() => handleOpenModal('deviceTest')} />

              <SectionSeparator />

              <InternetRequirements
                speed={CONTENT_OPTIONS[selectedOption].speed}
                connection={t(CONTENT_OPTIONS[selectedOption].connectionTranslationKey)}
              />

              <NetworkSpeedChecker selectedOption={selectedOption} onRunTest={() => handleOpenModal('internetTest')} />

              <div className={CN.banner} data-bem="UserNetwork__banner">
                <div className={CN.bannerTitle}>
                  {S.star} {t('staffAndBusiness.userNetwork.hardwareInformation.performanceBanner.title')}
                </div>
                <Bullets data={translatedDataBanner} list />
                {RENDER_LINK && (
                  <ExternalLink variant="inline" link={LINK_TBD}>
                    <div className={CN.link}>{t('staffAndBusiness.userNetwork.learnMoreButton')}</div>
                  </ExternalLink>
                )}
              </div>
              <div className={CN.textEnd}>
                {t('staffAndBusiness.userNetwork.hardwareInformation.liveChatGuide')}
                <Space>
                  <div className={CN.textBold}> {t('staffAndBusiness.userNetwork.hardwareInformation.liveChat')}</div>
                </Space>
              </div>
            </div>
          )}
        </div>
      </Foldout>
      <Foldout
        tag="UserNetwork__other"
        src={ICO_PHONE}
        title={t('staffAndBusiness.userNetwork.otherHardware.foldoutTitle')}
        isOpen={false}
        width="max"
      >
        <SectionSeparator />

        <div data-bem="UserNetwork__card-machine" className={CN.container}>
          <div>
            <img src={IMG_LOGO} alt="Slick logo" className={CN.imgLogo} />
            <div className={CN.bulletTitle}>{t('staffAndBusiness.userNetwork.otherHardware.section1Title')}</div>
            <Bullets data={translateDataCard(t)} hardware />
            <div className={CN.buttonContainer}>
              <NavLink
                to={linkTo({ pattern: LINK.slickPay, params: { id: salonId }, fault: '#' })}
                data-bem="UserNetwork__slickPayLink"
              >
                <Button variant="primary" xSpace>
                  {t('staffAndBusiness.userNetwork.learnMoreButton')}
                </Button>
              </NavLink>
            </div>
          </div>
          <div className={CN.imageContainer}>
            <img src={IMG_CARD} alt="" className={CN.imgDescription} />
          </div>
        </div>

        <SectionSeparator />

        <div data-bem="UserNetwork__slickPay-dock" className={CN.container}>
          <div>
            <div className={CN.bulletTitle}>{t('staffAndBusiness.userNetwork.otherHardware.section2Title')}</div>
            <Bullets data={translatedDataDock} hardware />
            <div className={CN.buttonContainer}>
              <ExternalLink variant="buttonPrimary" link={LINK_DOCK} xSpace>
                {t('staffAndBusiness.userNetwork.otherHardware.orderDockButton')}
              </ExternalLink>
            </div>
          </div>
          <div className={CN.imageContainer}>
            <img src={IMG_DOCK} alt="" className={CN.imgDescription} />
          </div>
        </div>

        <SectionSeparator />

        <div data-bem="UserNetwork__apple-app" className={CN.container}>
          <div>
            <div className={CN.bulletTitle}>{t('staffAndBusiness.userNetwork.otherHardware.section3Title')}</div>
            <Bullets data={translatedDataApp} hardware />
            <div className={CN.buttonContainer}>
              <NavLink
                to={linkTo({ pattern: LINK.stocksManager, params: { salonId }, fault: '#' })}
                data-bem="UserNetwork__stocksManager"
              >
                <Button variant="primary" xSpace>
                  {t('staffAndBusiness.userNetwork.learnMoreButton')}
                </Button>
              </NavLink>
            </div>
          </div>
          <div className={CN.imageContainer}>
            <img src={IMG_APP} alt="" className={CN.imgDescription} />
          </div>
        </div>
      </Foldout>
      <div className={CN.tAndC} data-bem="UserNetwork__tandc">
        <div className={CN.tAndCTitle}>{t('staffAndBusiness.userNetwork.termsAndConditions')} </div>
        {t('staffAndBusiness.userNetwork.termsAndCondTextPart1')}
        <ExternalLink variant="inline" link={LINK_TC}>
          <div className={CN.linkTc}>{t('staffAndBusiness.userNetwork.termsAndConditions')},</div>
        </ExternalLink>
        {t('staffAndBusiness.userNetwork.termsAndCondTextPart2')}
      </div>

      <SalonModal tag="DeviceChecker" isOpen={renderModal} variant="noFooter">
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button variant="empty" onClick={hideModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="alert" width="lg">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t(modalContent.titleTranslationKey)}</div>
            <div className={SM.modalText}>{t(modalContent.textTranslationKey)}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

export default UserNetwork;
